import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Heart, XCircle, Loader2 } from "lucide-react";

// Paystack bounces back here after a donation. We re-verify server-side
// (never trusting the client-visible reference alone) and show a
// friendly thank-you with a jump back to the film. Mirrors the regular
// payment-callback flow but uses the donation verify endpoint so the
// 70/30 split is applied by the right fulfiller.
export default function DonateCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | success | failed
  const [amount, setAmount] = useState(null);
  const [filmId, setFilmId] = useState(null);

  useEffect(() => {
    const ref = params.get("reference") || params.get("trxref") || params.get("tx_ref");
    if (!ref) {
      setState("failed");
      return;
    }
    api.get(`/payments/verify-donation/${ref}`).then((r) => {
      if (r.data.status === "success") {
        setAmount(r.data.currency === "USD"
          ? `$${((r.data.amount_usd_cents || 0) / 100).toFixed(2)}`
          : `₦${(r.data.amount_naira || 0).toLocaleString()}`);
        setFilmId(r.data.film_id);
        setState("success");
      } else {
        setState(r.data.status === "pending" ? "pending" : "failed");
      }
    }).catch(() => setState("failed"));
  }, [params]);

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-32 px-6 pb-20 max-w-xl mx-auto text-center" data-testid="donate-callback">
        {state === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-pink-400 mb-6" />
            <h1 className="font-display text-3xl font-bold">Confirming your donation…</h1>
            <p className="text-foreground/60 mt-2 text-sm">Hold tight — we're confirming the payment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-pink-500/15 border border-pink-400/40 flex items-center justify-center mb-6">
              <Heart className="w-10 h-10 text-pink-400 fill-pink-400" />
            </div>
            <h1 className="font-display text-3xl font-bold">Thank you so much.</h1>
            <p className="text-foreground/70 mt-2">
              {amount ? `Your ${amount} donation` : "Your donation"} just made this filmmaker's day.
              70% has been credited straight to their wallet.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              {filmId && (
                <Button onClick={() => navigate(`/film/${filmId}`)} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="back-to-film-btn">
                  Back to the film
                </Button>
              )}
              <Button variant="ghost" onClick={() => navigate("/browse")} className="border border-white/10" data-testid="donate-browse-btn">
                Browse more films
              </Button>
            </div>
          </>
        )}
        {state === "pending" && (
          <div data-testid="donate-callback-pending">
            <Loader2 className="w-12 h-12 mx-auto text-gold mb-6" />
            <h1 className="font-display text-3xl font-bold">Payment still processing.</h1>
            <p className="text-foreground/60 mt-2 text-sm">
              Your payment provider hasn't confirmed it yet. If it goes through, the filmmaker is credited automatically — no need to pay again.
            </p>
            <Button onClick={() => navigate("/browse")} className="mt-6 bg-pink-500 hover:bg-pink-600 text-white" data-testid="donate-pending-browse-btn">
              Browse more films
            </Button>
          </div>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-400 mb-6" />
            <h1 className="font-display text-3xl font-bold">Donation didn't go through.</h1>
            <p className="text-foreground/60 mt-2 text-sm">
              Nothing was charged. Please try again, or reach out to support if the issue repeats.
            </p>
            <Button onClick={() => navigate(-1)} className="mt-6 bg-pink-500 hover:bg-pink-600 text-white" data-testid="donate-retry-btn">
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
